import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { useAtom } from 'jotai';
import { bindPopover, usePopupState } from 'material-ui-popup-state/hooks';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router';

import CheckPermissions from '@/components/CheckPermissions';
import InlineEditSelect from '@/components/InlineEditSelect';
import InlineEditText from '@/components/InlineEditText';
import LoadingIndicator from '@/components/LoadingIndicator';
import TimeDisplay from '@/components/TimeDisplay';
import { PERMISSIONS } from '@/constants/auth.constants';
import { DATE_FORMAT, DATE_TIME_CALC_FORMAT } from '@/constants/date.constants';
import TicketAssignee from '@/features/TicketAssignee';
import {
  CATEGORY_ID,
  isKanatiServiceTicket,
  KANATI_ADVISORY_DISCLAIMER
} from '@/features/TicketCreateDialog/TicketCreateDialog.constants';
import TicketDetailsActivityLog from '@/features/TicketDetailsActivityLog';
import TicketDetailsAdditionalDetails from '@/features/TicketDetailsAdditionalDetails';
import TicketDetailsConversation from '@/features/TicketDetailsConversation';
import TicketDetailsDescription from '@/features/TicketDetailsDescription';
import TicketDetailsRecommendations from '@/features/TicketDetailsRecommendations';
import TicketDetailsStatusAlert from '@/features/TicketDetailsStatusAlert';
import TicketDetailsSubTopbarBottom from '@/features/TicketDetailsSubTopbar/TicketDetailsSubTopbarBottom';
import TicketDetailsSubTopbarTop from '@/features/TicketDetailsSubTopbar/TicketDetailsSubTopbarTop';
import TicketNextAction from '@/features/TicketNextAction/TicketNextAction';
import TicketProgress from '@/features/TicketProgress';
import TicketResponseActions from '@/features/TicketResponseActions';
import { TicketTags } from '@/features/TicketTags';
import TicketWatchers from '@/features/TicketWatchers';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import MainContent from '@/layout/MainContent';
import { useFetchSettings } from '@/pages/Administration/Administration.store';
import { selectedOrganizationIdAtom } from '@/pages/Organizations';
import {
  CUSTOMER_FACING_TICKET_STATUSES,
  TICKET_ORIGINATION_SOURCE,
  TICKET_STATUS,
  TICKET_STATUS_ID
} from '@/pages/Tickets/Tickets.constants';
import { useFetchTicketEntities } from '@/pages/Tickets/Tickets.store';
import { isLayoutMobileAtom } from '@/store/layout';
import { userAtom } from '@/store/user.store';

import { AI_SOC_TEAM_QUEUE } from '../Ticket.constants';
import {
  ticketAtom,
  ticketErrorAtom,
  ticketIsEditingAtom,
  useFetchAssigneesList,
  useFetchTicket,
  useFetchWatchersList,
  useUpdateAssignee,
  useUpdateCustomerTicketId,
  useUpdateFollowupDate,
  useUpdateNextAction,
  useUpdateTags,
  useUpdateTeamQueue,
  useUpdateTicketWatchers
} from '../Ticket.store';
import type { Watcher } from '../Ticket.types';
import { hasOrganizationWatchers, setFollowupDate } from '../Ticket.utils';
import TicketDetailsContext from './TicketDetails.store';
import useStyles from './TicketDetails.styles';

const TicketDetails = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [
    {
      assignee,
      activities,
      feedback_rating,
      feedback_reason,
      customer_ticket_id,
      id: ticketId,
      followup_date,
      next_action,
      origination_source,
      organization,
      owner,
      published_at,
      tags,
      team_queue,
      ticket_category,
      ticket_priority,
      ticket_response_actions,
      ticket_status,
      ticket_type,
      ticket_watchers,
      updated_at
    }
  ] = useAtom(ticketAtom);
  const [ticketError, setTicketError] = useAtom(ticketErrorAtom);
  const [, setTicketIsEditing] = useAtom(ticketIsEditingAtom);
  const [selectedOrganizationId, setSelectedOrganizationId] = useAtom(
    selectedOrganizationIdAtom
  );
  const [userData] = useAtom(userAtom);
  const canUpdateTicket = usePermissionsCheck(PERMISSIONS.TICKETS_UPDATE);
  const { watchersPageOptions } = useFetchWatchersList(
    'page',
    'selectedTicketOrg'
  );
  const { data: settings } = useFetchSettings(organization?.label);
  const { data: assignees } = useFetchAssigneesList(organization?.label || '');

  const location = useLocation();
  const minHeightMatches = useMediaQuery('(min-height:1170px)');
  const { id } = useParams();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'followupPicker'
  });
  const nextActionAnchorRef = useRef<HTMLDivElement | null>(null);
  const { classes } = useStyles({ isLayoutMobile });

  //-----------------------------------------------------------------
  const ticket_status_name = ticket_status?.name as TICKET_STATUS;

  let parsedTicketId: number;
  if (id) {
    parsedTicketId = parseInt(id, 10);
  } else {
    parsedTicketId = 1;
  }

  const { isFetching, isLoading, refetch } = useFetchTicket(
    parsedTicketId,
    location.pathname
  );

  const { mutate: updateTicketWatchers } = useUpdateTicketWatchers(refetch);
  const { mutate: updateFollowupDate } = useUpdateFollowupDate();
  const { mutate: updateNextAction } = useUpdateNextAction();
  const { mutate: updateAssignee } = useUpdateAssignee();
  const { mutate: updateTeamQueue } = useUpdateTeamQueue();
  const { data: ticketEntities } = useFetchTicketEntities();
  const {
    mutate: updateCustomerTicketId,
    isPending: isCustomerTicketIdSaving
  } = useUpdateCustomerTicketId();
  const { mutate: updateTags } = useUpdateTags();

  //-----------------------------------------------------------------
  const handleFollowupDateUpdate = (followupDate: Dayjs) => {
    if (!dayjs(followupDate).isSame(followup_date, 'day')) {
      updateFollowupDate(setFollowupDate(followupDate).format());
      popupState.close();
    }
  };

  const handleNextActionUpdate = (nextAction: string) => {
    if (nextAction !== next_action) {
      updateNextAction(nextAction);
      popupState.open(nextActionAnchorRef.current);
    }
  };

  const handleTicketAssigneeChange = useCallback(
    (assigneeId: number | null) => {
      updateAssignee(assigneeId);
    },
    [watchersPageOptions, organization]
  );

  const handleTeamQueueChange = useCallback(
    (newTeamQueue: string) => {
      updateTeamQueue(newTeamQueue);
    },
    [updateTeamQueue]
  );

  const teamQueueOptions = useMemo(
    () =>
      (ticketEntities?.team_queue || []).map((option) => ({
        ...option,
        disabled:
          option.value === AI_SOC_TEAM_QUEUE &&
          origination_source !== TICKET_ORIGINATION_SOURCE.AI_SOC
      })),
    [ticketEntities, origination_source]
  );

  const handleTagsChange = useCallback((newTags: string[]) => {
    updateTags(newTags);
  }, []);

  const handleCustomerTicketIdChange = (newCustomerTicketId: string | null) => {
    updateCustomerTicketId(newCustomerTicketId);
  };

  const handleTicketWatchersChange = useCallback(
    (ids: Watcher[]) => {
      const hasOrgWatchers = hasOrganizationWatchers(
        ids.map((w) => w.userId),
        watchersPageOptions,
        organization?.id || 0
      );

      if (hasOrgWatchers) {
        updateTicketWatchers(ids);
        setTicketError('');
      } else if (
        !hasOrgWatchers &&
        ![TICKET_STATUS.DRAFT, TICKET_STATUS.NEEDS_REVIEW].includes(
          ticket_status_name as TICKET_STATUS
        )
      ) {
        setTicketError(
          'Ticket must have at least one watcher from the organization'
        );
      }
    },
    [watchersPageOptions, organization]
  );

  //-----------------------------------------------------------------
  const closedTime = useMemo(() => {
    if (
      ticket_status_name === TICKET_STATUS.COMPLETED ||
      ticket_status_name === TICKET_STATUS.AUTO_CLOSED
    ) {
      // get closed ticket activity
      const closedTicketActivity = activities?.filter(
        (activity) =>
          activity.new_status === TICKET_STATUS_ID.COMPLETED ||
          activity.new_status === TICKET_STATUS_ID.AUTO_CLOSED
      );

      // return closed time if ticket is closed
      if (
        closedTicketActivity?.length !== undefined &&
        closedTicketActivity?.length > 0
      ) {
        return dayjs(closedTicketActivity[0].created_at).format(
          DATE_TIME_CALC_FORMAT
        );
      }

      // return updated time if ticket is not closed
      return dayjs(updated_at).format(DATE_TIME_CALC_FORMAT);
    }
    return null;
  }, [activities, updated_at]);

  const publishedTime = useMemo(() => {
    if (CUSTOMER_FACING_TICKET_STATUSES.includes(ticket_status_name)) {
      // get opened ticket activity
      const openedTicketActivity = activities?.filter(
        (activity) => activity.new_status === TICKET_STATUS_ID.OPEN
      );

      // return published time if ticket is open
      if (
        openedTicketActivity?.length !== undefined &&
        openedTicketActivity?.length > 0
      ) {
        return dayjs(published_at).format(DATE_TIME_CALC_FORMAT);
      }
    }
    return null;
  }, [activities, published_at]);

  const watcherCount = useMemo(() => {
    if (!userData?.internal) {
      return ticket_watchers.filter((watcher) => !watcher.internal).length;
    }
    return ticket_watchers.length;
  }, [ticket_watchers, userData]);

  useEffect(() => {
    setTicketIsEditing(false);
    setTicketError('');
  }, [setTicketIsEditing]);

  useEffect(() => {
    // set selected organization id to the ticket's organization id
    if (selectedOrganizationId !== 0) {
      setSelectedOrganizationId(organization?.id);
    }
  }, [organization]);

  return (
    <TicketDetailsContext.Provider value={refetch}>
      <LoadingIndicator isLoading={isLoading} />
      <TicketDetailsSubTopbarTop />
      {!isLayoutMobile && <TicketDetailsSubTopbarBottom />}
      <MainContent>
        {isLayoutMobile && <TicketDetailsSubTopbarBottom />}
        <div className={classes.ticketDetails}>
          <div className={classes.ticketDetailsColumnLeft}>
            <TicketDetailsStatusAlert />
            {isKanatiServiceTicket(ticket_type, ticket_category?.id) &&
              ticket_category?.id === CATEGORY_ID.KANATI_ADVISORY && (
                <Alert severity="info">{KANATI_ADVISORY_DISCLAIMER}</Alert>
              )}
            {ticketError && <Alert severity="error">{ticketError}</Alert>}
            <TicketDetailsDescription />
            {ticket_type === 1 && <TicketDetailsRecommendations />}
            {ticket_response_actions && ticket_response_actions.length > 0 && (
              <TicketResponseActions />
            )}
            <TicketDetailsAdditionalDetails />
            <TicketDetailsConversation />
          </div>
          <div className={classes.ticketDetailsColumnRight}>
            <div className={classes.columnRightSection}>
              <Typography variant="h6" component="h2">
                Ticket Progress
              </Typography>
              <TicketProgress />
            </div>
            <Divider />
            <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
              <div className={classes.columnRightSection}>
                <Typography variant="h6" component="h2">
                  Next Action
                </Typography>
                <FormControl id="next-action" fullWidth>
                  <TicketNextAction
                    onChange={handleNextActionUpdate}
                    value={next_action}
                  />
                </FormControl>
              </div>
              <div
                className={classes.columnRightSection}
                ref={nextActionAnchorRef}
              >
                <Typography variant="h6" component="h2">
                  Follow-Up
                </Typography>
                <div className={classes.columnRightSectionContent}>
                  <Typography variant="body1">
                    {followup_date
                      ? dayjs(followup_date).format(DATE_FORMAT)
                      : '--'}
                  </Typography>
                  <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left'
                    }}
                    marginThreshold={minHeightMatches ? 16 : 80}
                  >
                    <DialogTitle>Update Follow-Up</DialogTitle>
                    <LocalizationProvider
                      localeText={{
                        cancelButtonLabel: 'Skip',
                        okButtonLabel: 'Update'
                      }}
                    >
                      <FormControl id="next-action" fullWidth>
                        <StaticDatePicker
                          defaultValue={setFollowupDate(followup_date)}
                          disablePast
                          slotProps={{
                            actionBar: {
                              actions: ['cancel', 'accept']
                            },
                            toolbar: {
                              hidden: true
                            }
                          }}
                          onAccept={(followup) => {
                            if (followup) {
                              handleFollowupDateUpdate(followup);
                            }
                          }}
                        />
                      </FormControl>
                    </LocalizationProvider>
                  </Popover>
                </div>
              </div>
              <div className={classes.columnRightSection}>
                <Typography variant="h6" component="h2">
                  Owner
                </Typography>
                <div className={classes.columnRightSectionContent}>
                  <Typography variant="body1">{owner?.name || '--'}</Typography>
                </div>
              </div>
              <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
                <div className={classes.columnRightSection}>
                  <Typography variant="h6" component="h2">
                    Team Queue
                  </Typography>
                  <div className={classes.columnRightSectionContent}>
                    <InlineEditSelect
                      value={team_queue}
                      options={teamQueueOptions}
                      onSave={handleTeamQueueChange}
                      disabled={!canUpdateTicket}
                      emptyDisplay="--"
                      placeholder="Select team queue"
                    />
                  </div>
                </div>
              </CheckPermissions>
              <div className={classes.columnRightSection}>
                <Typography variant="h6" component="h2">
                  Originator
                </Typography>
                <div className={classes.columnRightSectionContent}>
                  <Typography variant="body1">
                    {origination_source || '--'}
                  </Typography>
                </div>
              </div>

              <div className={classes.columnRightSection}>
                <Typography variant="h6" component="h2">
                  Customer Ticket ID
                </Typography>
                <div className={classes.columnRightSectionContent}>
                  <InlineEditText
                    key={ticketId}
                    value={customer_ticket_id}
                    onSave={handleCustomerTicketIdChange}
                    isDataLoading={isFetching || isCustomerTicketIdSaving}
                    disabled={!canUpdateTicket}
                    maxLength={32}
                    emptyDisplay="--"
                    placeholder="Enter customer ticket ID"
                  />
                </div>
              </div>

              {settings?.enable_ticket_assignee && (
                <div className={classes.columnRightSection}>
                  <Typography variant="h6" component="h2">
                    Assignee
                  </Typography>
                  <div className={classes.columnRightSectionContent}>
                    <Typography variant="body1">
                      {assignee?.name || '--'}
                    </Typography>
                  </div>
                </div>
              )}
              {(settings?.tags?.[0] || tags?.[0]) && (
                <div className={classes.columnRightSection}>
                  <Typography variant="h6" component="h2">
                    Tags
                  </Typography>
                  <div className={classes.columnRightSectionContent}>
                    {settings?.tags?.[0] ? (
                      <TicketTags
                        tags={tags}
                        availableTags={settings?.tags || []}
                        onChange={handleTagsChange}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {tags?.map((tag) => (
                          <Chip key={tag} label={tag} variant="filled" />
                        ))}
                      </Box>
                    )}
                  </div>
                </div>
              )}

              {!!feedback_rating && (
                <div className={classes.columnRightSection}>
                  <Typography variant="h6" component="h2">
                    User Feedback
                  </Typography>
                  <div className={classes.columnRightSectionContent}>
                    <Stack spacing={1}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <FontAwesomeIcon
                          icon={
                            feedback_rating === 1 ? faThumbsUp : faThumbsDown
                          }
                        />
                        <Typography variant="body2">
                          {feedback_rating === 1 ? 'Good' : 'Not Good'}
                        </Typography>
                      </Box>
                      {feedback_reason && (
                        <Typography variant="caption" sx={{ mt: 0.5 }}>
                          <strong>Reason:</strong> {feedback_reason}
                        </Typography>
                      )}
                    </Stack>
                  </div>
                </div>
              )}

              <Divider />
            </CheckPermissions>

            <CheckPermissions permissions={PERMISSIONS.NOT_INTERNAL}>
              <div className={classes.columnRightSection}>
                <Typography variant="h6" component="h2">
                  Customer Ticket ID
                </Typography>
                <div className={classes.columnRightSectionContent}>
                  <InlineEditText
                    key={ticketId}
                    value={customer_ticket_id}
                    onSave={handleCustomerTicketIdChange}
                    isDataLoading={isFetching || isCustomerTicketIdSaving}
                    disabled={!canUpdateTicket}
                    maxLength={32}
                    emptyDisplay="--"
                    placeholder="Enter customer ticket ID"
                  />
                </div>
              </div>

              {settings?.enable_ticket_assignee && (
                <div className={classes.columnRightSection}>
                  <Typography variant="h6" component="h2">
                    Assignee
                  </Typography>
                  <TicketAssignee
                    onChange={handleTicketAssigneeChange}
                    assigneeOptions={assignees}
                    assignee={assignee}
                  />
                </div>
              )}

              {(settings?.tags?.[0] || tags?.[0]) && (
                <div className={classes.columnRightSection}>
                  <Typography variant="h6" component="h2">
                    Tags
                  </Typography>
                  <div>
                    {settings?.tags?.[0] ? (
                      <TicketTags
                        tags={tags}
                        availableTags={settings?.tags || []}
                        onChange={handleTagsChange}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {tags?.map((tag) => (
                          <Chip key={tag} label={tag} variant="filled" />
                        ))}
                      </Box>
                    )}
                  </div>
                </div>
              )}

              <Divider />
            </CheckPermissions>

            <div className={classes.columnRightSection}>
              <div className={classes.columnRightSectionContent}>
                <TimeDisplay
                  closedAt={closedTime}
                  publishedAt={publishedTime}
                />
              </div>
            </div>
            <Divider />
            <div className={classes.columnRightSection}>
              <Typography variant="h6" component="h2" gutterBottom>
                Watchers ({watcherCount})
              </Typography>
              <TicketWatchers
                buttonLabel="Update"
                onChange={handleTicketWatchersChange}
                ticketPriority={ticket_priority?.id}
                ticketOrganizationId={organization?.id}
                ticketType={ticket_type}
                watchers={ticket_watchers}
                watchersOptions={watchersPageOptions}
              />
            </div>
            <Divider />
            <div className={classes.columnRightSection}>
              <Typography variant="h6" component="h2" gutterBottom>
                Ticket Activity Log
              </Typography>
              <div className={classes.columnRightSectionContent}>
                <TicketDetailsActivityLog />
              </div>
            </div>
          </div>
        </div>
      </MainContent>
    </TicketDetailsContext.Provider>
  );
};

export default TicketDetails;
